import { useEffect, useState } from 'react';
import axios from 'axios';
import { Database, HardDrive, RefreshCw, ShieldCheck, Trash2, TriangleAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtBytes = (b) => {
  const n = Number(b || 0);
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} kB`;
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString('es-VE', { dateStyle: 'short', timeStyle: 'short' }) : 'nunca');

export default function DataHealthCard({ setToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/data-health`, { headers: headers() });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const purge = async () => {
    setPurging(true);
    try {
      const res = await axios.post(`${API}/data-health/purge-logs`, null, { headers: headers() });
      setToast?.({ type: 'success', message: `Purgadas ${res.data.total} fila(s) de logs y registros técnicos` });
      await load();
    } catch (e) {
      setToast?.({ type: 'error', message: 'No se pudo ejecutar la purga' });
    } finally {
      setPurging(false);
    }
  };

  if (loading) return null;
  if (!data) return null;

  const dirty = data.status === 'dirty';

  return (
    <div
      className={`mb-6 rounded-xl border p-5 ${dirty ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}
      data-testid="data-health-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          {dirty
            ? <TriangleAlert className="w-6 h-6 text-amber-600 mt-0.5 shrink-0" />
            : <ShieldCheck className="w-6 h-6 text-emerald-600 mt-0.5 shrink-0" />}
          <div>
            <h3 className={`font-bold ${dirty ? 'text-amber-900' : 'text-emerald-900'}`} data-testid="data-health-title">
              {dirty
                ? `Datos de prueba detectados en producción (${data.issues_count})`
                : 'Sin datos de prueba en producción'}
            </h3>
            <p className={`text-xs mt-1 max-w-2xl ${dirty ? 'text-amber-800' : 'text-emerald-800'}`}>
              Peso de la base, filas técnicas purgables y comercios o webhooks de prueba que
              hayan quedado activos. No incluye nada contable.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="data-health-refresh"
            className="text-xs px-3 py-1.5 border rounded-lg bg-white hover:bg-gray-50 flex items-center gap-1"
          ><RefreshCw className="w-3 h-3" /> Revisar</button>
          <button
            onClick={purge}
            disabled={purging || data.purgeable_total === 0}
            data-testid="data-health-purge"
            className="text-xs px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-40 flex items-center gap-1"
          ><Trash2 className="w-3 h-3" /> {purging ? 'Purgando…' : 'Purgar logs ahora'}</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <div className="bg-white rounded-lg border px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1">
            <HardDrive className="w-3 h-3" /> Peso de la base
          </p>
          <p className="text-xl font-bold text-gray-900" data-testid="data-health-db-size">
            {data.database?.available ? fmtBytes(data.database.total_bytes) : 'n/d'}
          </p>
          <p className="text-[11px] text-gray-500">
            {data.database?.available
              ? `${data.database.tables?.[0]?.table || ''} · ${fmtBytes(data.database.tables?.[0]?.bytes)}`
              : data.database?.reason}
          </p>
        </div>
        <div className="bg-white rounded-lg border px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1">
            <Database className="w-3 h-3" /> Filas purgables
          </p>
          <p className="text-xl font-bold text-gray-900" data-testid="data-health-purgeable">{data.purgeable_total}</p>
          <p className="text-[11px] text-gray-500">Retención: logs {data.retention?.log_info_days}d · errores {data.retention?.log_error_days}d</p>
        </div>
        <div className="bg-white rounded-lg border px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Comercios de prueba</p>
          <p className={`text-xl font-bold ${data.test_merchants.length ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="data-health-test-merchants">
            {data.test_merchants.length}
          </p>
          <p className="text-[11px] text-gray-500">{data.test_projects} proyecto(s) asociado(s)</p>
        </div>
        <div className="bg-white rounded-lg border px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Webhooks de prueba</p>
          <p className={`text-xl font-bold ${data.test_webhooks.length ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="data-health-test-webhooks">
            {data.test_webhooks.length}
          </p>
          <p className="text-[11px] text-gray-500">Última purga: {fmtDate(data.retention?.last_run)}</p>
        </div>
      </div>

      {data.ledger_gaps?.available && data.ledger_gaps.count > 0 && (
        <div className="mt-3 bg-white rounded-lg border border-red-300 px-3 py-3" data-testid="data-health-ledger-gaps">
          <p className="text-sm font-bold text-red-800">
            {data.ledger_gaps.count} orden(es) pagada(s) sin asiento en el ledger —{' '}
            {Number(data.ledger_gaps.amount).toLocaleString('es-VE', { minimumFractionDigits: 2 })} Bs
          </p>
          <p className="text-[11px] text-gray-600 mt-1">
            El pago entró al banco pero el crédito al ledger del comercio falló y no se reintentó.
            Reponer con <code className="font-mono">python scripts/backfill_ledger_gaps.py --apply</code>
          </p>
          <ul className="mt-2 space-y-0.5 text-[11px]">
            {data.ledger_gaps.items.map((g) => (
              <li key={g.order_id} className="flex justify-between gap-3">
                <span className="font-mono">{g.order_code} · {g.company_name}</span>
                <span className="text-gray-600 shrink-0">
                  {Number(g.amount).toLocaleString('es-VE', { minimumFractionDigits: 2 })} Bs · {fmtDate(g.paid_at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {dirty && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 text-xs text-amber-900 underline"
            data-testid="data-health-toggle"
          >{expanded ? 'Ocultar detalle' : 'Ver qué se detectó'}</button>
          {expanded && (
            <div className="mt-3 bg-white rounded-lg border p-3 space-y-3 text-xs" data-testid="data-health-detail">
              {data.test_merchants.length > 0 && (
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Comercios sin pagos reales</p>
                  <ul className="space-y-0.5">
                    {data.test_merchants.map((m) => (
                      <li key={m.user_id} className="flex justify-between gap-3">
                        <span className="font-mono truncate">{m.email}</span>
                        <span className="text-gray-500 shrink-0">{fmtDate(m.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.test_webhooks.length > 0 && (
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Webhooks activos hacia destinos de prueba</p>
                  <ul className="space-y-0.5">
                    {data.test_webhooks.map((w) => (
                      <li key={w.webhook_id} className="flex justify-between gap-3">
                        <span className="font-mono truncate">{w.url}</span>
                        <span className="text-gray-500 shrink-0">{fmtDate(w.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[11px] text-gray-500 pt-1 border-t">
                Para archivarlos: <code className="font-mono">python scripts/cleanup_test_data.py --apply</code>
                {' '}(dry-run por defecto, reversible, no toca órdenes ni ledger).
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
